/**
 * AppBanner
 * Non-blocking, auto-dismissing banner that slides in from the top.
 * Supports error, warning, info, and success types.
 * Driven by useAppBannerStore — renders at the app level.
 * Testable: has testID, no native Alert dependency.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { Animated, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from '@gluestack-ui/themed';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { FS_SM } from '../../theme';
import { useAppBannerStore, type BannerType } from '../../hooks/useAppBanner';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

const AUTO_DISMISS_MS: Record<BannerType, number> = {
  error: 5000,
  warning: 4000,
  info: 3000,
  success: 3000,
};

export function AppBanner() {
  const insets = useSafeAreaInsets();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const { visible, message, type, action, showCount } = useAppBannerStore((s) => s.banner);
  const dismiss = useAppBannerStore((s) => s.dismiss);

  // eslint-disable-next-line react-hooks/refs -- Animated.Value is a stable mutable object, reading .current during render is the standard RN pattern
  const translateY = useRef(new Animated.Value(-100)).current;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleDismiss = useCallback(() => {
    // Capture the showCount at the moment the slide-out animation
    // starts. If a fresh `showBanner` arrives during the 200 ms
    // animation (next-branch banner; user-initiated banner from
    // elsewhere), the store's showCount will have incremented by
    // the time `.start(...)`'s completion callback fires. We must
    // NOT call `dismiss()` in that case — doing so would wipe the
    // brand-new banner state with INITIAL_BANNER and the freshly-
    // shown banner would vanish before the user (or the E2E
    // assertion) could see it.
    //
    // CONV-009 regression net: in the 14:59 → 17:48 transition
    // (Validation Failed branch), Account Deleted's 5 s timer fired
    // at T=750449 and started the slide-out; Validation Failed's
    // grantConsent landed `showBanner` at T=750571; the slide-out
    // animation's completion at T=750649 then called dismiss(),
    // erasing the new banner. visible(app-banner, 5000ms) on the
    // runner then timed out because the banner had been alive for
    // ~80 ms before being clobbered.
    const startedAtCount = useAppBannerStore.getState().banner.showCount;
    Animated.timing(translateY, {
      toValue: -100,
      duration: 200,
      useNativeDriver: true,
    }).start(() => {
      const currentCount = useAppBannerStore.getState().banner.showCount;
      if (currentCount === startedAtCount) {
        dismiss();
      }
      // else: a newer showBanner arrived mid-animation; the new
      // banner's own effect run will animate it in. We just let
      // the slide-out animation we ran here be visually superseded
      // by the new slide-in (translateY ends at -100, the next
      // effect run will animate it back to 0).
    });
  }, [translateY, dismiss]);

  useEffect(() => {
    if (visible) {
      Animated.timing(translateY, {
        toValue: 0,
        duration: 250,
        useNativeDriver: true,
      }).start();

      timerRef.current = setTimeout(() => {
        handleDismiss();
      }, AUTO_DISMISS_MS[type]);
    } else {
      translateY.setValue(-100);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // `showCount` is in the dep list (not `message`) so EVERY showBanner
    // call resets the timer — even when the new banner has the exact
    // same message + type as the still-visible one. Without showCount,
    // a second consent-grant failure with the identical "Failed to
    // save consent. Please try again." string would inherit the first
    // call's pending dismiss timeout (CONV-009 regression after the
    // dev.set-auth swap tightened branch timing).
  }, [visible, type, showCount, handleDismiss, translateY]);

  if (!visible) return null;

  const colors = getBannerColors(type, theme.isDark);

  return (
    <Animated.View
      testID="app-banner"
      accessibilityRole="alert"
      style={[
        styles.container,
        {
          paddingTop: insets.top + 8,
          backgroundColor: colors.bg,
          transform: [{ translateY }],
        },
      ]}
    >
      <TouchableOpacity
        testID="app-banner-dismiss"
        activeOpacity={0.8}
        onPress={handleDismiss}
        style={styles.content}
      >
        <Text
          allowFontScaling={false}
          style={[
            styles.message,
            { fontSize: FS_SM * fontScale, color: colors.text, ...ffs },
          ]}
          numberOfLines={2}
        >
          {message}
        </Text>
        {action && (
          <TouchableOpacity
            testID="app-banner-action"
            onPress={() => {
              action.onPress();
              handleDismiss();
            }}
          >
            <Text
              allowFontScaling={false}
              style={[
                styles.actionText,
                { fontSize: FS_SM * fontScale, color: colors.text, ...ffs },
              ]}
            >
              {action.label}
            </Text>
          </TouchableOpacity>
        )}
      </TouchableOpacity>
    </Animated.View>
  );
}

function getBannerColors(type: BannerType, isDark: boolean) {
  switch (type) {
    case 'error':
      return { bg: isDark ? '#7F1D1D' : '#FEF2F2', text: isDark ? '#FECACA' : '#991B1B' };
    case 'warning':
      return { bg: isDark ? '#78350F' : '#FFFBEB', text: isDark ? '#FDE68A' : '#92400E' };
    case 'success':
      return { bg: isDark ? '#064E3B' : '#ECFDF5', text: isDark ? '#A7F3D0' : '#065F46' };
    case 'info':
    default:
      return { bg: isDark ? '#1E3A5F' : '#EFF6FF', text: isDark ? '#BFDBFE' : '#1E40AF' };
  }
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  message: {
    flex: 1,
    fontWeight: '500',
  },
  actionText: {
    fontWeight: '700',
    marginLeft: 12,
  },
});
